import { Trans, useTranslation } from 'react-i18next';

type MagicLinkSentProps = {
  email: string;
  resending: boolean;
  resendSecondsLeft: number;
  error: string | null;
  onResend: () => void;
  onUseDifferentEmail: () => void;
};

/** Post-submit confirmation — primary feedback that a magic link was sent. */
export function MagicLinkSent({
  email,
  resending,
  resendSecondsLeft,
  error,
  onResend,
  onUseDifferentEmail,
}: MagicLinkSentProps) {
  const { t } = useTranslation('auth');
  const resendLabel = resending
    ? t('magicLinkSent.sending')
    : resendSecondsLeft > 0
      ? t('magicLinkSent.resendIn', { seconds: resendSecondsLeft })
      : t('magicLinkSent.resend');

  return (
    <div className="text-center">
      <div
        className="mx-auto flex h-12 w-12 items-center justify-center rounded-full"
        style={{ backgroundColor: 'rgba(196, 92, 38, 0.12)', color: '#c45c26' }}
        aria-hidden
      >
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.75} stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>

      <h2 className="mt-6 text-[2rem] font-semibold tracking-[-0.03em] text-[#12141a]">
        {t('magicLinkSent.title')}
      </h2>
      <p className="mt-3 text-sm leading-relaxed text-[#12141a]/60">
        <Trans
          t={t}
          i18nKey="magicLinkSent.sentTo"
          values={{ email }}
          components={{ email: <span className="mt-1 block font-semibold text-[#12141a]" dir="ltr" /> }}
        />
      </p>
      <p className="mt-4 text-sm text-[#12141a]/45">{t('magicLinkSent.openLink')}</p>

      {error ? (
        <p className="mt-4 text-sm text-red-700" role="alert">
          {error}
        </p>
      ) : null}

      <div className="mt-8 space-y-3">
        <button
          type="button"
          disabled={resending || resendSecondsLeft > 0}
          onClick={onResend}
          className="w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition disabled:opacity-60"
          style={{ backgroundColor: '#c45c26' }}
        >
          {resendLabel}
        </button>
        <button
          type="button"
          onClick={onUseDifferentEmail}
          className="w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-[#12141a]/65 transition hover:text-[#12141a]"
        >
          {t('magicLinkSent.useDifferentEmail')}
        </button>
      </div>
    </div>
  );
}
